import React, { memo, useEffect, useState } from 'react'
import { removeToast, useToast, useLoading, ToastItem, setToastState } from '@/ui/useToastStore'
import { useCommon } from '@/core/useCommon'
import { AnimatePresence, motion } from 'framer-motion'
import { createPortal } from 'react-dom'
import { Loader2, X, CircleCheckIcon, CircleXIcon } from 'lucide-react'
import { useHideBodyScroll } from '@/core/useHideBodyScroll'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/styles'

const ToastVariants = cva('', {
  variants: {
    type: {
      danger: 'sm:bg-[#fef0f0] sm:text-[#f56c6c]',
      success: 'sm:bg-[#eefbf3] sm:text-[#16a34a]',
    },
  },
  defaultVariants: {
    type: 'danger',
  },
})

const Variants = {
  initial: { opacity: 0, scale: 0.5, transition: { duration: 0.3 } },
  animate: { opacity: 1, scale: 1, transition: { ease: 'backOut', duration: 0.3, delay: 0.1 } },
  exit: { opacity: 0, scale: 0.5, transition: { ease: 'easeInOut', duration: 0.2 } },
}

/** 全域 Toast 管理，單一版本，不堆疊顯示 */
export const ToastSingleManager = memo(() => {
  const { toasts } = useToast()

  /** 沒有訊息 */
  if (!toasts.length) return null

  /** 彈出訊息動畫 */
  return (
    <div className="pointer-events-none fixed top-0 left-0 z-280 flex h-1/2 w-full flex-col items-center justify-end sm:top-15 sm:h-0 sm:justify-start">
      <AnimatePresence>
        {toasts.map((item) => (
          <motion.div
            key={item.id}
            variants={Variants as any}
            initial="initial"
            animate={item.enabled ? 'animate' : 'exit'}
            className={cn(
              'rounded-8 text-16 pointer-events-auto my-8 flex max-w-200 cursor-pointer items-center justify-center bg-black/80 px-16 py-10 text-center text-white sm:min-w-370',
              // ToastVariants({ type: item.type }),
            )}
            onClick={() => removeToast(item.id)}>
            {/* {item.type === 'success' ? <CircleCheckIcon className="mr-10 hidden size-16 sm:block" /> : <CircleXIcon className="mr-10 hidden size-16 shrink-0 sm:block" />} */}
            <div className="flex items-center justify-center text-center break-words sm:min-h-48">{item.message}</div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
})

ToastSingleManager.displayName = 'ToastSingleManager'

const LoadingVariants = cva('flex h-96 w-96 items-center justify-center rounded-12', {
  variants: {
    mode: {
      default: '',
      fixed: 'bg-black/50',
      full: 'w-full h-full',
    },
  },
  defaultVariants: {
    mode: 'default',
  },
})

/** Loading 樣式 */
export const LoadingSpin = ({ mode, className, ...props }: React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof LoadingVariants>) => {
  return (
    <div className={cn(LoadingVariants({ mode }), className)} {...props}>
      <Loader2 className="h-48 w-48 animate-spin text-white" />
    </div>
  )
}

/** 全域 Loading 管理 */
export const LoadingManager = memo(() => {
  const { enabled } = useLoading()
  useHideBodyScroll(enabled)

  return createPortal(
    <AnimatePresence>
      {enabled && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          className="fixed top-0 left-0 z-300 flex h-full w-full items-center justify-center">
          <LoadingSpin mode="fixed" />
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
})

LoadingManager.displayName = 'LoadingManager'
